import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AssignmentsViewOptions,
  AssignmentsWorkTypeMap,
  MSAssignmentsStatusOptions,
} from 'src/interfaces/assignment.types';
import { AssignmentsViewState } from 'src/interfaces/store.types';
import type { RootState } from 'src/store';
import { useAppSelector } from 'src/store/redux';

const initialState: AssignmentsViewState = {
  activeAssignmentsView: AssignmentsViewOptions.TODO,
  isNoAssignmentsDataViewMS: null, // set to indicate that assignmentsData is not yet loaded,
  isNoAssignmentsDataToDo: null,
  isNoAssignmentsDataPastWork: null,
  activeMSAssignmentsStatus: null,
  activeMSAssignmentsWorkType: AssignmentsWorkTypeMap.TODO,
  activeMSAssignmentsSubject: null,
};

export const assignmentsViewSlice = createSlice({
  name: 'assignmentsView',
  initialState,
  reducers: {
    setActiveAssignmentsView: (
      state,
      action: PayloadAction<AssignmentsViewOptions>,
    ) => {
      state.activeAssignmentsView = action.payload;
    },
    setIsNoAssignmentsDataViewMS: (state, action: PayloadAction<boolean>) => {
      state.isNoAssignmentsDataViewMS = action.payload;
    },
    setIsNoAssignmentsDataToDo: (state, action: PayloadAction<boolean>) => {
      state.isNoAssignmentsDataToDo = action.payload;
    },
    setIsNoAssignmentsDataPastWork: (state, action: PayloadAction<boolean>) => {
      state.isNoAssignmentsDataPastWork = action.payload;
    },
    setActiveMSAssignmentsStatus: (
      state,
      action: PayloadAction<MSAssignmentsStatusOptions | null>,
    ) => {
      state.activeMSAssignmentsStatus = action.payload;
    },
    setActiveMSAssignmentsWorkType: (
      state,
      action: PayloadAction<AssignmentsWorkTypeMap>,
    ) => {
      state.activeMSAssignmentsWorkType = action.payload;
    },
    setActiveMSAssignmentsSubject: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.activeMSAssignmentsSubject = action.payload;
    },
  },
});

export const {
  setActiveAssignmentsView,
  setIsNoAssignmentsDataViewMS,
  setIsNoAssignmentsDataToDo,
  setIsNoAssignmentsDataPastWork,
  setActiveMSAssignmentsStatus,
  setActiveMSAssignmentsWorkType,
  setActiveMSAssignmentsSubject,
} = assignmentsViewSlice.actions;

export const useActiveAssignmentsViewSelector = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.activeAssignmentsView,
  );

export const useIsNoAssignmentsDataViewMSSelector = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.isNoAssignmentsDataViewMS,
  );

export const useIsNoAssignmentsDataToDoSelector = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.isNoAssignmentsDataToDo,
  );

export const useIsNoAssignmentsDataPastWorkSelector = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.isNoAssignmentsDataPastWork,
  );

export const useActiveMSAssignmentsWorkType = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.activeMSAssignmentsWorkType,
  );

export const useActiveMSAssignmentsStatus = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.activeMSAssignmentsStatus,
  );

export const useActiveMSAssignmentsSubject = () =>
  useAppSelector(
    (state: RootState) => state.assignmentsView.activeMSAssignmentsSubject,
  );

export default assignmentsViewSlice.reducer;
